import { OptionalMark } from "@patient-watch/forms";
import { supabaseClient } from "@/lib/supabase";
import { ui } from "@patient-watch/ui";
import type { InputT } from "@patient-watch/types";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const ADDRESS_INPUT_IDS = {
    line: "da7f1271-b8ab-4065-8dd0-a35d3c56c37e",
    postalCode: "898b8aed-68b9-4d1f-a0f4-1c6ebd4fa480",
    city: "b92c31de-965d-4714-a808-946f47f61ed4",
    region: "3882b8b6-b7cf-400f-b9a8-6f03c307ad6f",
    country: "00fef669-b89d-46ff-aafb-2cc74c8c89fd",
} as const;

interface AddressSuggestion {
    placeId: string;
    label: string;
    addressLine: string;
    postalCode: string;
    city: string;
    region: string;
    country: string;
}

interface Props {
    input: InputT;
    form: Record<string, any>;
}

function localeToGeoapify(locale: string): { lang: string; countryCode: string } {
    if (locale.startsWith("de")) return { lang: "de", countryCode: "de" };
    if (locale.startsWith("da")) return { lang: "da", countryCode: "dk" };
    return { lang: "en", countryCode: "gb" };
}

export default function AddressAutocompleteInput({ input, form }: Props) {
    const { locale } = useAppLocale();
    const { lang, countryCode } = localeToGeoapify(locale);
    const [query, setQuery] = useState(() => String(form.getValues(input.input_id) ?? ""));
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const appliedQuery = useRef<string | null>(null);

    useEffect(() => {
        const trimmed = query.trim();
        if (appliedQuery.current !== null && trimmed === appliedQuery.current) {
            setIsLoading(false);
            return;
        }

        if (trimmed.length < 3) {
            setSuggestions([]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setOpen(true);
        const timer = window.setTimeout(async () => {
            const { data, error } = await supabaseClient.functions.invoke("public-address-autocomplete", {
                body: { query: trimmed, lang, countryCode },
            });
            if (cancelled) return;
            setIsLoading(false);
            if (!error && Array.isArray(data?.suggestions)) {
                setSuggestions(data.suggestions);
                setOpen(true);
            } else {
                setSuggestions([]);
                setOpen(true);
            }
        }, 350);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [countryCode, lang, query]);

    const applySuggestion = (suggestion: AddressSuggestion, onChange: (value: string) => void) => {
        const displayValue = suggestion.label || suggestion.addressLine;
        appliedQuery.current = displayValue;
        onChange(displayValue);
        form.setValue(input.input_id, displayValue, { shouldDirty: true, shouldTouch: true });
        const siblings = {
            [ADDRESS_INPUT_IDS.line]: displayValue,
            [ADDRESS_INPUT_IDS.postalCode]: suggestion.postalCode,
            [ADDRESS_INPUT_IDS.city]: suggestion.city,
            [ADDRESS_INPUT_IDS.region]: suggestion.region,
            [ADDRESS_INPUT_IDS.country]: suggestion.country,
        };
        const registered = form.getValues() as Record<string, unknown>;
        for (const [name, value] of Object.entries(siblings)) {
            if (name === input.input_id) continue;
            if (!Object.hasOwn(registered, name)) continue;
            form.setValue(name, value, { shouldDirty: true, shouldTouch: true });
        }
        setQuery(displayValue);
        setSuggestions([]);
        setOpen(false);
        setIsLoading(false);
    };

    const showList = open && query.trim().length >= 3;

    return (
        <ui.FormField
            control={form.control}
            name={input.input_id}
            render={({ field }) => (
                <ui.FormItem className="relative">
                    <ui.FormLabel>
                        {input.input_label}
                        {input.is_optional && <OptionalMark />}
                    </ui.FormLabel>
                    {input.input_description && <ui.FormDescription>{input.input_description}</ui.FormDescription>}
                    <div className="relative">
                        <ui.FormControl>
                            <ui.Input
                                {...field}
                                value={field.value ?? ""}
                                autoComplete="street-address"
                                className="pr-10"
                                data-testid={`${input.input_id}-input`}
                                role="combobox"
                                aria-autocomplete="list"
                                aria-busy={isLoading}
                                aria-expanded={showList}
                                onBlur={() => window.setTimeout(() => setOpen(false), 150)}
                                onChange={(event) => {
                                    appliedQuery.current = null;
                                    field.onChange(event);
                                    setQuery(event.target.value);
                                }}
                                onFocus={() => setOpen(query.trim().length >= 3)}
                            />
                        </ui.FormControl>
                        {isLoading && (
                            <span
                                aria-hidden
                                className="pointer-events-none absolute inset-y-0 right-3 flex items-center"
                            >
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </span>
                        )}
                    </div>
                    {showList && (
                        <ul
                            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
                            data-testid="address-suggestions"
                            role="listbox"
                        >
                            {isLoading && suggestions.length === 0 ? (
                                <li className="px-3 py-2 text-lg text-muted-foreground">Searching addresses...</li>
                            ) : suggestions.length === 0 ? (
                                <li className="px-3 py-2 text-lg text-muted-foreground">No addresses found</li>
                            ) : (
                                suggestions.map((suggestion) => (
                                    <li key={suggestion.placeId}>
                                        <button
                                            className="w-full rounded-sm px-3 py-2 text-left text-lg hover:bg-accent focus:bg-accent focus:outline-none"
                                            data-testid="address-suggestion"
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                            }}
                                            onClick={() => applySuggestion(suggestion, field.onChange)}
                                            role="option"
                                            type="button"
                                            aria-selected="false"
                                        >
                                            {suggestion.label}
                                        </button>
                                    </li>
                                ))
                            )}
                        </ul>
                    )}
                    <ui.FormMessage />
                </ui.FormItem>
            )}
        />
    );
}
